import { Nullable } from '@crossbeam/types';

import { DateTime } from 'luxon';

import { MDM_PROPERTIES } from '@/constants/mdm';
import {
  FilterPart,
  Population,
  StandardPopulationType,
} from '@/types/populations';
import { Source, SourceDataType } from '@/types/sources';

export const MDM_DEFAULT_FIELDS = {
  account: new Set([MDM_PROPERTIES.account.NAME, 'company_website']),
  contact: new Set(['person_email']),
  lead: new Set(['person_email']),
};

export const OPERATORS = [
  {
    label: 'is',
    id: 'in',
    labelOverrides: {
      datetime: 'is on',
    },
  },
  {
    label: 'is not',
    id: 'not in',
    labelOverrides: {
      datetime: 'is not on',
    },
  },
  {
    label: 'greater than',
    id: '>',
    labelOverrides: {
      datetime: 'is after',
    },
  },
  {
    label: 'less than',
    id: '<',
    labelOverrides: {
      datetime: 'is before',
    },
  },
  {
    label: 'greater than or equal to',
    id: '>=',
    labelOverrides: {
      datetime: 'is on or after',
    },
  },
  {
    label: 'less than or equal to',
    id: '<=',
    labelOverrides: {
      datetime: 'is on or before',
    },
  },
  {
    label: 'is empty',
    id: 'is empty',
    filterPartOverrides: {
      operator: 'is empty',
      operand: null,
    },
    labelOverrides: {},
  },
  {
    label: 'is not empty',
    id: 'is not empty',
    filterPartOverrides: {
      operator: 'is not empty',
      operand: null,
    },
    labelOverrides: {},
  },
  {
    label: 'contains',
    id: 'contains',
    labelOverrides: {},
  },
  {
    label: 'does not contain',
    id: 'does not contain',
    labelOverrides: {},
  },
  {
    label: 'is one of',
    id: 'OR',
    labelOverrides: {},
  },
  {
    label: 'is all of',
    id: 'AND',
    labelOverrides: {},
  },
];

export const COMBINATORS = [
  { label: 'and', id: 'AND' },
  { label: 'or', id: 'OR' },
];

export const DATA_TYPES: Record<
  NonNullable<SourceDataType>,
  {
    label: string;
    example?: string;
    placeholder?: string;
    validate: (value: string) => boolean;
    transform: (value: string) => string | number | boolean | null;
    transformOperator?: (operator: string) => string;
    operators: string[];
    values?: { id: string; label: string }[];
  }
> = {
  string: {
    label: 'Type a value to filter',
    placeholder: 'Type a value to filter',
    validate: (value: unknown) => {
      // note it would be very strange if something that was not a string was passed here
      return typeof value === 'string';
    },
    transform: (value: string) => value,
    operators: [
      'in',
      'not in',
      'null',
      'is empty',
      'not null',
      'is not empty',
      'contains',
      'does not contain',
    ],
  },
  number: {
    label: 'number',
    example: '(100, 200.1, 100.24, etc)',
    placeholder: 'e.g. 10, 10.22',
    validate: (value: string) => {
      return /^\d+(\.\d{1,9})?$/.test(value) || !value;
    },
    transform: (value: string) => parseFloat(value),
    transformOperator: (operator: string) => operator,
    operators: [
      'in',
      'not in',
      '>',
      '<',
      '>=',
      '<=',
      'null',
      'is empty',
      'not null',
      'is not empty',
    ],
  },
  datetime: {
    label: 'date in YYYY-MM-DD format',
    example: '(2019-01-03 is January 3, 2019)',
    validate: (value: string) => {
      const date = DateTime.fromISO(value);
      return date.isValid || !value;
    },
    // Adjusts datetime values so that time is always set to midnight.
    transform: (value: string) => {
      return DateTime.fromISO(value, { zone: 'utc' }).toString();
    },
    transformOperator: (operator: string) => {
      let newOperator = operator;
      switch (operator) {
        case '<=':
          newOperator = '<';
          break;
        case '>':
          newOperator = '>=';
          break;
        default:
          break;
      }
      return newOperator;
    },
    operators: [
      'in',
      'not in',
      '>',
      '<',
      '>=',
      '<=',
      'null',
      'is empty',
      'not null',
      'is not empty',
    ],
  },
  boolean: {
    label: 'boolean value',
    example: '(true, false)',
    placeholder: 'e.g. true or false',
    validate: (value: string) =>
      /^true$/i.test(value) || /^false$/i.test(value) || !value,
    // this transform function requires `value` be a string
    transform: (value: string) => {
      return value ? value === 'true' : null;
    },
    transformOperator: (operator: string) => operator,
    operators: ['in', 'not in', 'is empty', 'is not empty'],
    values: [
      { id: 'true', label: 'true' },
      { id: 'false', label: 'false' },
    ],
  },
  segment_filter: {
    label: 'segment filter',
    validate: () => true,
    transform: (value: string) => value,
    operators: ['in'],
  },
  our_segment_filter: {
    label: 'our segment filter',
    validate: () => true,
    transform: (value: string) => value,
    operators: ['AND', 'OR'],
  },
  partner_segment_filter: {
    label: 'partner segment filter',
    validate: () => true,
    transform: (value: string) => value,
    operators: ['AND', 'OR'],
  },
};

export const DELIMITER = '\\';

export function emptyPopulation({
  baseSchema,
  baseTable,
  sourceId,
  standardType,
}: {
  baseSchema: string;
  baseTable: string;
  sourceId: number;
  standardType?: Nullable<StandardPopulationType>;
}) {
  const population: Partial<Population> & { sources: Source[] } = {
    base_schema: baseSchema,
    base_table: baseTable,
    source_id: sourceId,
    sources: [],
    standard_type: standardType,
  };
  population.source_filter_expressions = {};
  return population;
}

export function validateAndConvertOperandDataTypes(
  dataType: SourceDataType | undefined,
  operands: FilterPart['operand'],
) {
  if (operands == null)
    return { operandValidationErrors: [], convertedOperands: null };
  const trimOperand = (
    dataType: SourceDataType | undefined,
    item: FilterPart['operand'],
  ) => {
    // For dates we need to clear out the time portion
    if (item && dataType === 'datetime' && typeof item !== 'string') {
      return DateTime.fromJSDate(item as Date).toFormat('yyyy-MM-dd');
    }
    if (!dataType) return item;
    if (item || typeof item === 'boolean' || item === 0) {
      return item.toString().trim();
    }
    return '';
  };
  if (!Array.isArray(operands)) {
    const trimmedOperand = trimOperand(dataType, operands);
    const hasErrors = dataType
      ? !DATA_TYPES[dataType]?.validate(trimmedOperand as string)
      : false;
    const operandValidationErrors = [];
    if (hasErrors) {
      operandValidationErrors.push(trimmedOperand);
    }
    const convertedOperands = dataType
      ? DATA_TYPES[dataType]?.transform(trimmedOperand as string)
      : trimmedOperand;

    return { operandValidationErrors, convertedOperands };
  }
  const trimmedOperands = operands.map((operand) =>
    trimOperand(dataType, operand),
  );
  const operandValidationErrors = trimmedOperands
    .map((item) => {
      const operandHasError = dataType
        ? !DATA_TYPES[dataType]?.validate(item as string)
        : false;
      return { operandHasError, operand: item };
    })
    .filter((item) => item.operandHasError);

  const convertedOperands = trimmedOperands.map((item) => {
    return dataType ? DATA_TYPES[dataType]?.transform(item as string) : item;
  });
  return { operandValidationErrors, convertedOperands };
}
